/**
 * IRLAssessmentTab.tsx
 *
 * 6-dimension IRL (Innovation Readiness Level) Assessment with radar chart.
 * Left column: Chart.js radar chart (60%)
 * Right column: Dimension sliders + strategy recommendation + notes (40%)
 *
 * Dimensions: TRL, BRL, IPRRL, TmRL, FRL, CRL (each 0-9)
 * Strategy: Auto-computed from average (S1: 0-2, S2: 3-4, S3: 5-6, S4: 7-9)
 */

import { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import { Radar } from 'react-chartjs-2'
import { authFetchJson } from '../../lib/authFetch'
import { IRLAssessment, IRL_DIMENSIONS, IRLDimension } from '../../types'
import { InlineNote } from '../ui/InlineNote'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

interface IRLAssessmentTabProps {
  projectId: number
}

// ─── Dimension metadata ─────────────────────────────────────────────────────

interface DimensionMeta {
  code: IRLDimension
  label: string
  description: string
}

const DIMENSION_META: Record<IRLDimension, DimensionMeta> = {
  trl: {
    code: 'trl',
    label: 'Technology Readiness',
    description: 'Technical maturity of the solution',
  },
  brl: {
    code: 'brl',
    label: 'Business Readiness',
    description: 'Business model and go-to-market maturity',
  },
  iprrl: {
    code: 'iprrl',
    label: 'IP & Regulatory Readiness',
    description: 'IP protection and regulatory pathway',
  },
  tmrl: {
    code: 'tmrl',
    label: 'Team Readiness',
    description: 'Team completeness and capabilities',
  },
  frl: {
    code: 'frl',
    label: 'Funding Readiness',
    description: 'Funding status and runway',
  },
  crl: {
    code: 'crl',
    label: 'Customer Readiness',
    description: 'Customer validation and traction',
  },
}

// ─── Level hint system ──────────────────────────────────────────────────────

interface LevelHint {
  label: string
  color: string
}

const getLevelHint = (value: number): LevelHint => {
  if (value === 0) return { label: 'Not assessed', color: '#6b7280' }
  if (value <= 2) return { label: 'Early stage', color: '#E61B25' }
  if (value <= 4) return { label: 'Developing', color: '#F59E0B' }
  if (value <= 6) return { label: 'Established', color: '#F59E0B' }
  if (value <= 8) return { label: 'Advanced', color: '#10B981' }
  return { label: 'Mature', color: '#10B981' }
}

// ─── Strategy recommendation ────────────────────────────────────────────────

interface StrategyRec {
  id: string
  name: string
  description: string
  borderColor: string
}

const getStrategy = (average: number): StrategyRec => {
  if (average < 3) {
    return {
      id: 's1',
      name: 'S1: Incubation',
      description: 'Focus on fundamental research and proof of concept',
      borderColor: '#E61B25',
    }
  }
  if (average < 5) {
    return {
      id: 's2',
      name: 'S2: Acceleration',
      description: 'Ready for structured development program',
      borderColor: '#F59E0B',
    }
  }
  if (average < 7) {
    return {
      id: 's3',
      name: 'S3: Growth',
      description: 'Scale operations and market entry',
      borderColor: '#F59E0B',
    }
  }
  return {
    id: 's4',
    name: 'S4: Scale',
    description: 'Optimize and expand market presence',
    borderColor: '#10B981',
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

export function IRLAssessmentTab({ projectId }: IRLAssessmentTabProps) {
  const [assessment, setAssessment] = useState<IRLAssessment | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Local state for sliders
  const [trl, setTrl] = useState(0)
  const [brl, setBrl] = useState(0)
  const [iprrl, setIprrl] = useState(0)
  const [tmrl, setTmrl] = useState(0)
  const [frl, setFrl] = useState(0)
  const [crl, setCrl] = useState(0)
  const [notes, setNotes] = useState('')

  // Fetch assessment on mount
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await authFetchJson<{ assessment: IRLAssessment | null }>(
          `/api/v2/projects/${projectId}/irl`
        )
        const { assessment } = response
        if (assessment) {
          setAssessment(assessment)
          setTrl(assessment.trl)
          setBrl(assessment.brl)
          setIprrl(assessment.iprrl)
          setTmrl(assessment.tmrl)
          setFrl(assessment.frl)
          setCrl(assessment.crl)
          setNotes(assessment.notes || '')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load assessment')
      } finally {
        setLoading(false)
      }
    }
    fetchAssessment()
  }, [projectId])

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const response = await authFetchJson<{ assessment: IRLAssessment }>(
        `/api/v2/projects/${projectId}/irl`,
        {
          method: 'PUT',
          json: {
            trl,
            brl,
            iprrl,
            tmrl,
            frl,
            crl,
            notes,
          },
        }
      )
      setAssessment(response.assessment)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assessment')
    } finally {
      setSaving(false)
    }
  }

  // Compute average IRL
  const values = [trl, brl, iprrl, tmrl, frl, crl]
  const average = values.reduce((a, b) => a + b, 0) / values.length
  const strategy = getStrategy(average)

  // Chart data
  const chartData = {
    labels: [
      DIMENSION_META.trl.label,
      DIMENSION_META.brl.label,
      DIMENSION_META.iprrl.label,
      DIMENSION_META.tmrl.label,
      DIMENSION_META.frl.label,
      DIMENSION_META.crl.label,
    ],
    datasets: [
      {
        label: 'IRL Assessment',
        data: [trl, brl, iprrl, tmrl, frl, crl],
        fill: true,
        backgroundColor: 'rgba(230, 27, 37, 0.15)',
        borderColor: '#E61B25',
        pointBackgroundColor: '#E61B25',
        pointBorderColor: '#E61B25',
        pointRadius: 4,
        pointHoverRadius: 5,
        tension: 0.3,
      },
    ],
  }

  const chartOptions: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        min: 0,
        max: 9,
        ticks: {
          stepSize: 3,
          font: { size: 11, family: "'Inter', sans-serif" },
          color: '#a8a8c0',
        },
        grid: {
          color: '#2a2a44',
        },
        pointLabels: {
          font: { size: 11, weight: 500 as any, family: "'Inter', sans-serif" },
          color: '#a8a8c0',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(30, 30, 56, 0.95)',
        titleColor: '#e8e8f0',
        bodyColor: '#c8c8d8',
        borderColor: '#2a2a44',
        borderWidth: 1,
        padding: 8,
        titleFont: { size: 12, weight: 600 as any },
        bodyFont: { size: 12 },
      },
    },
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#a8a8c0]">Loading assessment...</p>
      </div>
    )
  }

  return (
    <div className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-6">
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-[#E61B25]/10 border border-[#E61B25]/30 rounded-lg text-[#E61B25] text-sm">
          {error}
        </div>
      )}

      {/* 2-column grid: radar (60%) + sliders (40%) */}
      <div className="grid grid-cols-[3fr_2fr] gap-8">
        {/* LEFT: Radar Chart */}
        <div className="flex items-center justify-center">
          <div style={{ maxHeight: '400px', width: '100%' }}>
            <Radar data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* RIGHT: Sliders + Strategy + Notes */}
        <div className="flex flex-col gap-6">
          {/* Dimension Sliders */}
          <div className="space-y-6">
            {/* TRL */}
            <DimensionSlider
              dimension="trl"
              value={trl}
              onChange={setTrl}
              meta={DIMENSION_META.trl}
            />
            {/* BRL */}
            <DimensionSlider
              dimension="brl"
              value={brl}
              onChange={setBrl}
              meta={DIMENSION_META.brl}
            />
            {/* IPRRL */}
            <DimensionSlider
              dimension="iprrl"
              value={iprrl}
              onChange={setIprrl}
              meta={DIMENSION_META.iprrl}
            />
            {/* TmRL */}
            <DimensionSlider
              dimension="tmrl"
              value={tmrl}
              onChange={setTmrl}
              meta={DIMENSION_META.tmrl}
            />
            {/* FRL */}
            <DimensionSlider
              dimension="frl"
              value={frl}
              onChange={setFrl}
              meta={DIMENSION_META.frl}
            />
            {/* CRL */}
            <DimensionSlider
              dimension="crl"
              value={crl}
              onChange={setCrl}
              meta={DIMENSION_META.crl}
            />
          </div>

          {/* Strategy Recommendation Card */}
          <div
            className="bg-[#12122a] rounded-xl p-4 border-l-4"
            style={{ borderLeftColor: strategy.borderColor }}
          >
            <h3 className="font-semibold text-[#e8e8f0] text-sm mb-1">{strategy.name}</h3>
            <p className="text-[#a8a8c0] text-[12px] leading-relaxed">{strategy.description}</p>
            <p className="text-[#6b7280] text-[11px] mt-2">
              Average: {average.toFixed(1)} / 9
            </p>
          </div>
        </div>
      </div>

      {/* Notes Section (full width) */}
      <div className="mt-8 pt-6 border-t border-[#2a2a44]">
        <h3 className="text-[#e8e8f0] font-semibold text-sm mb-3">Assessment Notes</h3>
        <InlineNote
          value={notes}
          onChange={setNotes}
          placeholder="Add assessment notes, context, or key findings..."
          editable={true}
          minHeight={100}
        />
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`
            px-6 py-2.5
            bg-[#E61B25] text-white
            rounded-lg
            font-medium text-sm
            transition-colors
            ${saving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#d41620]'}
          `}
        >
          {saving ? 'Saving...' : 'Save Assessment'}
        </button>
      </div>
    </div>
  )
}

// ─── Dimension Slider Component ──────────────────────────────────────────────

interface DimensionSliderProps {
  dimension: IRLDimension
  value: number
  onChange: (value: number) => void
  meta: DimensionMeta
}

function DimensionSlider({ dimension, value, onChange, meta }: DimensionSliderProps) {
  const hint = getLevelHint(value)

  return (
    <div className="space-y-2">
      {/* Label + Current value */}
      <div className="flex justify-between items-center">
        <label className="text-[#e8e8f0] text-sm font-medium">{meta.label}</label>
        <span className="text-[#e8e8f0] font-bold text-sm">{value}</span>
      </div>

      {/* Slider */}
      <input
        type="range"
        min="0"
        max="9"
        step="1"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right,
            #E61B25 0%, #E61B25 ${(Math.min(value, 2) / 9) * 100}%,
            #F59E0B ${Math.max((Math.min(value, 6) / 9) * 100, (2 / 9) * 100)}%,
            #F59E0B ${Math.max((Math.min(value, 6) / 9) * 100, (2 / 9) * 100)}%,
            #10B981 ${Math.max((value / 9) * 100, (6 / 9) * 100)}%,
            #10B981 100%)`,
        }}
      />

      {/* Hint text */}
      <p className="text-[12px]" style={{ color: hint.color }}>
        {hint.label}
      </p>
    </div>
  )
}

export default IRLAssessmentTab
